// The address a person pastes as their calendar feed: what is accepted, and how
// it is shown back.
//
// Dependency-free, and shared by the Worker (`ics-run.ts`) and the app's
// Account sheet (`AccountBody`'s Calendar section), so the two cannot disagree
// about what a usable address is. The app imports it by relative path, as it
// already does `districts.ts`.
//
// The address is a secret: anyone holding it can read the calendar. Nothing
// here puts it in a message; `maskIcsUrl` is the only form a screen draws.

export type IcsUrlCheck = { ok: true; url: string } | { ok: false; note: string };

/**
 * A pasted address, made fetchable or refused.
 *
 * `webcal://` is what iCloud and Apple Calendar hand out and is `https://` by
 * another name, so it is rewritten rather than refused. Plain `http://` is
 * refused: a secret address sent in the clear is not worth syncing. Credentials
 * in the URL and bare IP literals / dotless hosts are refused — a private feed
 * has a public hostname, and nothing a person pastes should steer the Worker
 * at an address that is not one.
 */
export function normaliseIcsUrl(raw: string): IcsUrlCheck {
  let text = raw.trim();
  if (!text) return { ok: false, note: 'Paste the calendar address first.' };
  text = text.replace(/^webcals?:\/\//i, 'https://');
  if (/^http:\/\//i.test(text)) return { ok: false, note: 'The address must start with https://.' };
  let u: URL;
  try {
    u = new URL(text);
  } catch {
    return { ok: false, note: 'That does not look like a web address.' };
  }
  if (u.protocol !== 'https:') return { ok: false, note: 'The address must start with https://.' };
  if (u.username || u.password) return { ok: false, note: 'The address must not carry a username or password.' };
  const host = u.hostname.toLowerCase();
  // `[::1]` and `10.0.0.1` have no place in a calendar feed; `localhost` and
  // dotless names are the same thing spelled otherwise.
  if (host.startsWith('[') || /^\d+(\.\d+){3}$/.test(host) || !host.includes('.') || host.endsWith('.local')) {
    return { ok: false, note: 'The address needs a public host name.' };
  }
  const url = u.toString();
  if (url.length > 2000) return { ok: false, note: 'That address is too long.' };
  return { ok: true, url };
}

/** What a screen may draw of a saved address: the host and nothing after it.
 *  The path and query are the secret. */
export function maskIcsUrl(url: string): string {
  try {
    return `${new URL(url).hostname} / ••••••••`;
  } catch {
    return '••••••••';
  }
}
